<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div class="container-fluid section-p70" v-if="groupData">
    <div class="row">
      <div class="col-auto title-blue">
        {{ t("MANAGE_GROUP") }} - {{ groupData.title }}
      </div>
      <div class="col align-self-center">
        <Button
            v-if="!isGroupArchived"
            :label="t('ACTIVE')"
            v-tooltip="canBeArchived ? t('ARCHIVE') : ''"
            :disabled="!canBeArchived"
            @click="showArchiveConfirmation"
        />
        <Button
            v-else
            :label="t('ARCHIVED')"
            severity="danger"
            v-tooltip="canBeUnarchived ? t('UNARCHIVE') : ''"
            :disabled="!canBeUnarchived"
            @click="showUnArchiveConfirmation"
        />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-xl-3">
        <Card class="manage-group-base-card">
          <template #title>{{ t("BASE_DATA") }}</template>

          <template #content>
            <form id="groupForm" ref="form">
              <div class="mb-2" v-if="groupData.training">
                <label for="module" class="form-label">{{ t('MODULE') }}</label>
                <InputText id="module" v-model="groupData.training.translations[locale]" disabled class="w-100"/>
              </div>

              <div class="mb-2">
                <label for="groupTitle" class="form-label">{{ t('GROUP_TITLE') }}*</label>
                <InputText id="groupTitle" v-model="groupData.title" required
                           :disabled="isGroupArchived || !isAdminOrSchoolManager()" class="w-100"/>
              </div>

              <div class="mb-2">
                <label class="form-label" for="groupSize">{{ t("GROUP_SIZE") }}</label>
                <InputNumber id="groupSize" :showButtons="true" :min="groupData.trainees?.length ?? 1" v-model="groupData.groupSize"
                             :disabled="isGroupArchived" inputClass="w-100" class="d-block"/>
              </div>

              <div class="mb-2">
                <label for="location" class="form-label">{{ t('LOCATION') }}</label>
                <Select label-id="location" :options="getGroupLocationOptions(t)" v-model="groupData.location"
                        option-label="text" option-value="value"
                        :disabled="isGroupArchived || !isAdminOrSchoolManager()" class="w-100"/>
              </div>

              <div class="mb-2">
                <label for="address" class="form-label">{{ t('ADDRESS') }}*</label>
                <InputText id="address" v-model="groupData.address"
                           :disabled="isGroupArchived || !isAdminOrSchoolManager() || groupData.location === GroupLocation.ONLINE" class="w-100"/>
              </div>

              <div class="mb-2">
                <label for="language" class="form-label">{{ t('LANGUAGE') }}</label>
                <Select label-id="language" :options="getGroupLangOptions(t)" v-model="groupData.language"
                        option-label="text" option-value="value"
                        :disabled="isGroupArchived || !isAdminOrSchoolManager()" class="w-100"/>
              </div>

              <div class="mb-2">
                <label for="groupStatus" class="form-label">{{ t('STATUS') }}</label>
                <Select v-if="groupData.location !== GroupLocation.IN_HOUSE" label-id="groupStatus"
                        :options="statusOptionsWithArchived" v-model="groupData.status"
                        option-label="text" option-value="value"
                        :disabled="isGroupArchived || !isAdminOrSchoolManager()" class="w-100"/>
                <InputText v-else id="groupStatus" disabled :placeholder="t('PRIVATE')" class="w-100"/>
              </div>

            <div v-if="groupData.training && groupData.training.type !== 'SINGLE'" class="row mb-2">
              <div class="col-12">
                <label class="form-label" for="lessons">{{ t("MIN_HOURS") }}</label>
                <InputNumber id="lessons" :showButtons="true" min="1" v-model="groupData.lessons"
                             @input="validateLessonsNumber()"
                             :disabled="groupData.status === 'ARCHIVED' || !isAdminOrSchoolManager()"
                             inputClass="w-100" class="d-block"/>
              </div>
            </div>
              <Button :label="t('SAVE')" class="w-100"
                      :disabled="isGroupArchived || !isAdminOrSchoolManager()" @click="updateData()"/>
            </form>
          </template>
        </Card>
      </div>

      <div class="col-xl-9">
        <Tabs value="0" lazy>
          <TabList>
            <Tab value="0">{{ t('REGISTRANTS') }}</Tab>
            <Tab value="1">{{ t('TOR') }}</Tab>
            <Tab value="2">{{ t('ATTENDANCE') }}</Tab>
            <Tab value="3" v-if="auth.hasSomeRole(Role.ADMIN, Role.SCHOOL_MANAGER, Role.VIEWER)">{{ t('DOCUMENTS') }}</Tab>
            <Tab value="4">{{ t('TRAINER') }}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <div class="row">
                <div class="col text24-blue">
                  <span>{{ t("OCCUPANCY") }}</span>
                  <span class="ms-2">{{ groupData.trainees?.length ?? 0 }} / {{ groupData.groupSize }}</span>
                </div>

                <div class="col text-end">
                  <Button :label="t('PRINT')" icon="pi pi-chevron-down" iconPos="right"
                          :disabled="!groupData.trainees?.length" @click="printMenuRef?.toggle($event)"/>
                  <Menu ref="printMenuRef" :model="printMenuItems" :popup="true"/>
                  <Button v-if="isAdminOrSchoolManager()" :label="t('ADD')" class="ms-1"
                          @click="toggleSearchForm" :disabled="isGroupArchived"/>
                  <Button v-if="isAdminOrSchoolManager() || isTrainer()" :label="t('NOTIFICATION')" class="ms-1"
                          @click="showGroupNotificationModal"
                          :disabled="isGroupArchived || isViewer() || !groupData.trainees?.length"/>
                </div>
              </div>

              <div class="row" v-if="addTrainee">
                <div class="col">
                  <form class="searchForm">
                    <div class="mb-2">
                      <label for="search-trainees" class="form-label">{{ t('PERSON_SEARCH_TIP') }}</label>
                      <SearchInput input-id="search-trainees" :on-input="searchTrainee"
                                   @on-change="submitTrainee" :confirm-on-select-text="t('ADD_TRAINEE')" :required-symbols="3"/>
                    </div>
                  </form>
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <DataTable
                      :value="traineesList ?? []"
                      :rowHover="true"
                      :removableSort="true"
                      striped-rows
                      size="small"
                  >
                    <template #empty>
                      {{ t('NO_RESULTS') }}
                    </template>

                    <Column :header="t('NAME')" field="traineeFullName" sortable>
                      <template #body="{ data }">
                        <div v-if="data.problem" v-tooltip="t(data.problem)" class="d-inline-block">
                          <span class="text-danger">{{ data.trainee?.fullName }}</span>
                          <i class="pi pi-exclamation-triangle text-danger ms-2"/>
                        </div>
                        <span v-else>{{ data.trainee?.fullName }}</span>
                      </template>
                    </Column>

                    <Column :header="t('SSN')" field="traineeSsn" sortable>
                      <template #body="{ data }">
                        <router-link :to="{ name: 'Trainee', params: { id: data.trainee?.id } }">{{ data.trainee?.ssn }}</router-link>
                      </template>
                    </Column>

                    <Column :header="t('CITIZENSHIPS')">
                      <template #body="{ data }">
                        <citizenship-list :citizenships="data.trainee?.citizenships"/>
                      </template>
                    </Column>

                    <Column :header="t('PHONE')">
                      <template #body="{ data }">
                        {{ data.trainee?.phone }}
                      </template>
                    </Column>

                    <Column :header="t('EMAIL')">
                      <template #body="{ data }">
                        {{ data.trainee?.email }}
                      </template>
                    </Column>

                    <Column :header="t('INV_SENT')">
                      <template #body="{ data }">
                      <span
                          v-tooltip="canToggleInvitation ? getInvitationTooltip(data) : ''"
                          class="d-inline-block"
                          :class="{ 'inv-toggle': canToggleInvitation }"
                          @click="canToggleInvitation && toggleInvitationSent(data)"
                      >
                        <i class="pi pi-check text24-black" v-if="data.isInvitationSent"/>
                        <i class="pi pi-times text24-black" v-else/>
                      </span>
                      </template>
                    </Column>

                    <Column :header="t('DETAILS')">
                      <template #body="{ data }">
                        <div class="d-flex align-items-center flex-wrap">
                          <Button v-if="isAdminOrSchoolManager()"
                                  v-tooltip="t('TRANSFER_PERSON_FROM_GROUP', {name: data.trainee?.fullName})"
                                  icon="pi pi-arrows-h" text rounded :disabled="isGroupArchived" @click="showTransferTraineeModal(data)" class="me-2"/>
                          <Button v-if="isAdminOrSchoolManager()"
                                  v-tooltip="t('REMOVE_PERSON_FROM_GROUP', {name: data.trainee?.fullName})"
                                  icon="pi pi-times" text rounded :disabled="isGroupArchived" @click="showRemoveTraineeModal(data)" class="me-2" severity="danger"/>
                          <Button v-tooltip="t('DETAILS')"
                                  icon="pi pi-info-circle" text rounded @click="showEnrolmentDetailsModal(data)" class="me-2" severity="info"/>
                        </div>
                      </template>
                    </Column>
                  </DataTable>
                </div>
              </div>

              <remove-trainee-modal
                  v-if="isAdminOrSchoolManager()"
                  v-on:remove-trainee="removeTrainee"
              ></remove-trainee-modal>

              <transfer-trainee-modal
                  :current="route.params.id"
                  v-if="isAdminOrSchoolManager()"
                  v-on:transfer-trainee="transferTrainee"
              ></transfer-trainee-modal>

              <group-notification-modal ref="groupNotificationModalRef" @after-save="getGroupData"/>
            </TabPanel>

            <TabPanel value="1">
              <Card>
                <template #content>
                  <Card class="mb-3" v-if="groupData.training.type !== 'SINGLE'">
                    <template #content>
                      <div class="mb-2">
                        {{ t('PLANNED_ACADEMIC_HOURS') }}:
                        {{ (calculateTotalMinutes(trainingEvents) / 45).toFixed(2) }}
                      </div>

                      <div class="mb-2" :class="{ 'text-danger fw-bold': isMaxTotalHoursExceed }">
                        {{ t('ROUNDED_ACADEMIC_HOURS') }}: {{ roundedTotalTrainingEventHours }}
                      </div>

                      <div>
                        {{ t('TOTAL_DURATION') }}: {{ totalHoursAllowed }}
                      </div>
                    </template>
                  </Card>

                  <ul v-if="trainingEvents?.length" class="list-group list-group-flush">
                    <li v-for="(event, index) in trainingEvents" :key="event.id" class="list-group-item py-1 tor-event-item">
                      <div class="row align-items-center clickable" @click="toggleEventExpand(event)">
                        <div class="col">{{ event.startTime ? t(niceWeekday(new Date(event.startTime))) : t('INVALID_DATE') }}</div>
                        <div class="col">{{ event.startTime ? dateFormat(new Date(event.startTime)) : t('INVALID_DATE') }}</div>
                        <div class="col">{{ event.startTime && event.endTime ? timeFormat(new Date(event.startTime)) + ' - ' + timeFormat(new Date(event.endTime)) : t('INVALID_DATE') + ' - ' + t('INVALID_DATE') }}</div>
                        <Button v-if="!isGroupArchived" icon="pi pi-pencil" text rounded size="small" class="col-auto"/>
                        <Button v-if="groupData.training.type !== 'SINGLE' && !isGroupArchived" icon="pi pi-times" severity="danger" text rounded size="small" class="col-auto ms-2" @click.stop="removeTrainingEvent(event)"/>
                      </div>

                      <div v-if="expandedEventId === event.id" class="p-3 tor-event-edit">
                        <div class="mb-2">
                          <label class="form-label" :for="'event-date-' + index">{{ t('SELECT_DATE') }}</label>
                          <DatePicker :inputId="'event-date-' + index" v-model="changeDate" dateFormat="dd.mm.yy" showIcon class="w-100"/>
                        </div>
                        <div class="mb-2">
                          <label class="form-label" :for="'event-start-time-' + index">{{ t('SELECT_TIME') }}</label>
                          <DatePicker :inputId="'event-start-time-' + index" v-model="startTimeNew" timeOnly showIcon class="w-100"/>
                        </div>
                        <div class="mb-2">
                          <label class="form-label" :for="'event-end-time-' + index">{{ t('SELECT_TIME') }}</label>
                          <DatePicker :inputId="'event-end-time-' + index" v-model="endTimeNew" timeOnly showIcon class="w-100"/>
                        </div>
                        <Button :label="t('SAVE')" :disabled="!canSaveEditedEvent" @click="updateTrainingEvent(event)" v-tooltip="!canSaveEditedEvent ? t('ALL_FIELDS') : ''"/>
                      </div>
                    </li>
                  </ul>

                  <Paginator
                      v-if="showPaginationForGroupEvents"
                      :first="(currentPage - 1) * eventsPerPage"
                      :rows="eventsPerPage"
                      :total-records="totalRows"
                      :alwaysShow="false"
                      @page="onEventsPage"
                  />

                  <Card v-if="groupData.training.type !== 'SINGLE'" class="mt-3">
                    <template #content>
                      <div class="mb-2">
                        <label class="form-label" for="id-add-new-event-date">{{ t('ADD_NEW_EVENT') }}</label>
                        <div class="row">
                          <div class="col-lg-4 mb-2">
                            <DatePicker input-id="id-add-new-event-date" v-model="changeDateAdd" dateFormat="dd.mm.yy" :show-icon="true" :minDate="new Date()" class="w-100" :placeholder="t('DATE')"/>
                          </div>
                          <div class="col-lg-4 mb-2">
                            <DatePicker v-model="startTimeAdd" timeOnly showIcon class="w-100" :placeholder="t('SELECT_TIME')"/>
                          </div>
                          <div class="col-lg-4 mb-2">
                            <DatePicker v-model="endTimeAdd" timeOnly showIcon class="w-100" :placeholder="t('SELECT_TIME')"/>
                          </div>
                        </div>
                        <Button :label="t('ADD')" :disabled="!changeDateAdd || !startTimeAdd || !endTimeAdd || !isStartBeforeEndForAdd || !isAddDateValid" @click="addTrainingEvent" class="mt-2"/>
                      </div>
                    </template>
                  </Card>
                </template>
              </Card>
            </TabPanel>

            <TabPanel value="2">
              <abacus :group="groupData"/>
            </TabPanel>

            <TabPanel v-if="auth.hasSomeRole(Role.ADMIN, Role.SCHOOL_MANAGER, Role.VIEWER)" value="3">
              <training-group-documents :group-id="route.params.id"/>
            </TabPanel>

            <TabPanel value="4">
              <TrainerTab
                  tabTitle="TRAINER"
                  :trainers="groupData?.trainers || []"
                  :isArchived="isGroupArchived"
                  :trainerFields="trainerFields"
                  :trainingId="groupData?.training?.id"
                  :allowedToEdit="isAdminOrSchoolManager()"
                  @removeTrainer="removeTrainer"
                  @submitTrainer="addTrainerToGroup"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>

    <AddTraineeToGroupModal
        id="add-trainee-to-group-modal"
        ref="addTraineeToGroupModalRef"
        :after-save="afterSubmitTrainee"
    />

    <enrolment-details-modal ref="enrolmentDetailsModalRef"/>
    <file-downloader ref="fileDownloaderRef"/>
  </div>
</template>

<script setup>

import {computed, onBeforeMount, ref} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import eventBus from "@/util/eventBus";
import {useRoute} from "vue-router";

import JsPDF from "jspdf";
import autotable from "jspdf-autotable";
import moment from "moment";
import Abacus from "@/views/Abacus.vue";
import {PTSansFont} from "@/PTSansFontString";

import removeTraineeModal from "@/views/components/modals/removeTraineeModal.vue";
import transferTraineeModal from "@/views/components/modals/transferTraineeModal.vue";
import groupNotificationModal from "@/views/components/modals/groupNotificationModal.vue";
import TrainerTab from "@/views/components/TrainerTab.vue";
import {getGroupLangOptions, getGroupLocationOptions} from "@/model/group";
import {GroupLocation} from "@/model/group-location";
import SearchInput from "@/components/SearchInput.vue";
import AddTraineeToGroupModal from "@/views/components/AddTraineeToGroupModal.vue";
import {Role} from "@/auth/oauth";
import {useToast} from "@/util/toastUtils";
import TrainingGroupDocuments from "@/views/components/TrainingGroupDocuments.vue";
import FileDownloader from "@/components/FileDownloader.vue";
import EnrolmentDetailsModal from "@/views/components/modals/EnrolmentDetailsModal.vue";
import CitizenshipList from "@/views/components/CitizenshipList.vue";
import {useDateFormat} from "@/mixins/dateFormatMixin2.js";
import useTimeFormat from "@/mixins/timeFormatMixin2.js";
import useWeekdayFormat from "@/mixins/weekdayMixin2.js";

const axios = injectAxios();
const {t, locale} = useI18n();
const auth = injectAuth();
const route = useRoute();
const toast = useToast();
const props = defineProps(["group"]);

const groupData = ref(null);
const totalRows = ref(null);
const addTrainee = ref(false);
const trainingEvents = ref([]);
const startTimeNew = ref(null); // Date | null for Calendar timeOnly
const endTimeNew = ref(null);
const changeDate = ref(null);
const eventsPerPage = ref(200);
const currentPage = ref(1);
const startTimeAdd = ref(null);
const endTimeAdd = ref(null);
const changeDateAdd = ref(null);
const addTraineeToGroupModalRef = ref(null);
const fileDownloaderRef = ref(null);
const enrolmentDetailsModalRef = ref(null);
const groupNotificationModalRef = ref(null);

const {dateFormat} = useDateFormat();
const {timeFormat} = useTimeFormat();
const {niceWeekday} = useWeekdayFormat();

const isAdminOrSchoolManager = () => {
  return auth.hasRole("ADMIN") || auth.hasRole("SCHOOL_MANAGER");
};

const isViewer = () => {
  return auth.hasRole("VIEWER");
};

const isTrainer = () => {
  return auth.hasRole("TRAINER");
};

onBeforeMount(async () => {
  await getGroupData();
  await getGroupEvents(1);
});

const statusOptions = computed(() => {
  return [
    {value: "DRAFT", text: t("DRAFT")},
    {value: "PRIVATE", text: t("PRIVATE")},
    {value: "PUBLIC", text: t("PUBLIC")},
  ];
});

const traineesList = computed(() => {
  return groupData.value?.trainees?.map((t) => ({
    ...t,
    traineeFullName: t.trainee?.fullName,
    traineeSsn: t.trainee?.ssn,
  }));
});

const statusOptionsWithArchived = computed(() => {
  const opts = [...statusOptions.value];

  if (groupData.value?.status === "ARCHIVED") {
    opts.push({value: "ARCHIVED", text: t("ARCHIVED")});
  }

  return opts;
});

const printMenuRef = ref(null);
const printMenuItems = computed(() => [
  {label: "PDF", command: createPdf, disabled: !groupData.value?.trainees?.length},
  {label: "EXCEL", command: createExcel},
]);

const expandedEventId = ref(null);
const editingEvent = ref(null);

function toggleEventExpand(event) {
  if (expandedEventId.value === event.id) {
    expandedEventId.value = null;
    editingEvent.value = null;
    return;
  }

  expandedEventId.value = event.id;
  editingEvent.value = event;
  changeDate.value = event.startTime ? new Date(event.startTime) : null;
  startTimeNew.value = event.startTime ? moment(event.startTime).format("HH:mm") : null;
  endTimeNew.value = event.endTime ? moment(event.endTime).format("HH:mm") : null;
}

function onEventsPage(e) {
  const page = Math.floor(e.first / e.rows) + 1;
  getGroupEvents(page);
}

const trainerFields = computed(() => {
  return [
    {key: "fullName", label: t("NAME"), sortable: true},
    {key: "ssn", label: t("SSN"), sortable: true},
    {key: "phone", label: t("PHONE"), sortable: true},
    {key: "email", label: t("EMAIL"), sortable: true},
  ];
});

const showPaginationForGroupEvents = computed(() => {
  return Math.ceil(totalRows.value / eventsPerPage.value) > 1;
});

const getGroupEvents = async (page) => {
  const params = {page: page - 1, size: eventsPerPage.value};
  const resp = await axios.get(`/training/group/events/${route.params.id}`, {params: params});
  currentPage.value = page;
  totalRows.value = resp.data.totalSize;
  trainingEvents.value = resp.data.content;
};

const isGroupArchived = computed(() => {
  return groupData.value?.status === "ARCHIVED";
});

const canToggleInvitation = computed(() => {
  return !isGroupArchived.value && (isAdminOrSchoolManager() || isTrainer());
});

const getInvitationTooltip = (data) => {
  if (data.isInvitationSent) {
    const status = data.isInvitationSentManually ? t('INV_SENT_MANUALLY') : t('INV_SENT');
    return `${status} – ${t('INV_CLICK_REMOVE')}`;
  }
  return `${t('INV_UNSENT')} – ${t('INV_CLICK_CHECK')}`;
}

const toggleInvitationSent = async (data) => {
  await axios.patch('/training/group/invitation', {
    accountId: data.trainee.id,
    groupId: route.params.id,
    isInvitationSent: !data.isInvitationSent,
  });
  await getGroupData();
};

function showGroupNotificationModal() {
  if (isGroupArchived.value) {
    return;
  }

  groupNotificationModalRef.value.showModal(groupData.value.trainees);
}

function showRemoveTraineeModal(trainee) {
  if (isGroupArchived.value) {
    return;
  }

  eventBus.$emit("show-remove-trainee-modal", trainee.trainee);
}

function showTransferTraineeModal(trainee) {
  if (isGroupArchived.value) {
    return;
  }

  eventBus.$emit(
      "show-transfer-trainee-modal",
      trainee.trainee,
      groupData.value.training.code
  );
}

const canBeArchived = computed(() => {
  return isAdminOrSchoolManager() && groupData.value?.canBeArchived;
});

const canBeUnarchived = computed(() => {
  return auth.hasRole(Role.ADMIN);
});

function showArchiveConfirmation() {
  const data = {
    msg: t("GROUP_ARCHIVE_CONFIRM_MSG"),
    callback: archiveGroup,
  };

  eventBus.$emit("show-confirmation-modal", data);
}

function showUnArchiveConfirmation() {
  const data = {
    msg: t("GROUP_UNARCHIVE_CONFIRM_MSG"),
    callback: unArchiveGroup,
  };

  eventBus.$emit("show-confirmation-modal", data);
}

const archiveGroup = async () => {
  const response = await axios.post(`/training/group/${groupData.value.id}/archive`);
  groupData.value = response.data;
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"))
};

const unArchiveGroup = async () => {
  const response = await axios.post(`/training/group/${groupData.value.id}/unarchive`);
  groupData.value = response.data;
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"))
};

function validateLessonsNumber() {
  if (groupData.value.lessons < 1) {
    groupData.value.lessons = 1;
  }
}

function toggleSearchForm() {
  addTrainee.value = addTrainee.value === false;
}

const searchTrainee = async (searchValue) => {
  const params = {
    fields: "SSN,NAME",
    searchValue: searchValue,
  };

  const response = await axios.get(`/account/search/trainees`, {params: params});
  return response.data.content
      .filter((t) => t.isArchived === false)
      .map((t) => ({
        value: t.id,
        ssn: t.ssn,
        label: `${t.fullName} (${t.ssn})`,
      }));
};

const submitTrainee = async function (trainee) {
  const requestParams = {
    accountId: trainee.value,
  };

  try {
    await axios.post(`/training/group/${route.params.id}/trainee`, {}, {params: requestParams});
  } catch (e) {
    if (e.response?.data?.adminCanOverrideError && auth.hasRole(Role.ADMIN)) {
      const title = t("ADD_TRAINEE_WITH_SSN_TO_GROUP", {ssn: trainee.ssn});
      addTraineeToGroupModalRef.value.openModal(title, route.params.id, trainee.value);
    }

    throw e;
  }

  await afterSubmitTrainee();
};

const afterSubmitTrainee = async () => {
  await getGroupData();
  toast.makeSuccessToast(t("SAVED"));
  addTrainee.value = false;
};

const showEnrolmentDetailsModal = (item) => {
  enrolmentDetailsModalRef.value.openModal(item);
};

const transferTrainee = async (dataObj) => {
  const body = {
    accountId: dataObj.trainee.id,
    destinationGroupId: dataObj.destination,
    reason: dataObj.reason,
  };

  const response = await axios.post(`/training/group/${route.params.id}/transfer`, body);
  groupData.value = response.data;
  toast.makeSuccessToast(t("TRANSFERRED"));
};

const removeTrainee = async (dataObj) => {
  const body = {
    accountId: dataObj.trainee.id,
    reasonType: dataObj.type,
    reason: dataObj.reason,
  };

  const response = await axios.delete(`/training/group/${route.params.id}/trainee`, {data: body});
  groupData.value = response.data;
  toast.makeSuccessToast(t("REMOVED"));
};

const getGroupData = async () => {
  const response = await axios.get(`/training/group/${route.params.id}`);
  groupData.value = response.data;
};

const addTrainerToGroup = async (trainer) => {
  const response = await axios.post(
      `/training/group/${route.params.id}/trainer/${trainer.value}`,
  );
  toast.makeSuccessToast(t("SAVED"));
  groupData.value.trainers = response.data.trainers;
};

const removeTrainer = async (id) => {
  await axios.delete(
      `/training/group/${route.params.id}/trainer/${id}`,
  );
  toast.makeSuccessToast(t("REMOVED"));
  await getGroupData();
}

const updateData = async () => {
  if (groupData.value.location === GroupLocation.IN_HOUSE) {
    groupData.value.status = "PRIVATE";
  }
  const response = await axios.patch(
      `/training/group/` + groupData.value.id,
      groupData.value,
  );
  toast.makeSuccessToast(t("SAVED"));
  groupData.value = response.data;
  return groupData.value;
};

const createExcel = async () => {
  await fileDownloaderRef.value.downloadFile("/attendance/group/" + groupData.value.id + "/excel", {}, "Koolitusgruppide_arvestustabel.xlsx");
};

function createPdf() {
  const trainees = getPdfTraineeData();
  const imagePromises = getImages();

  const doc = new JsPDF("landscape");

  doc.addFileToVFS("PTSans.ttf", PTSansFont);
  doc.addFont("PTSans.ttf", "PTSans", "normal");
  doc.setFont("PTSans");

  Promise.all(imagePromises).then((images) => {
    const isLang = groupData.value.training.isLanguageTraining === true;
    const head = isLang
        ? [
          [
            "",
            "Given name(s) / Имя",
            "Surname / Фамилия",
            "Estonian personal code / \nЭстонский личный код",
            "Signature / Подпись \nparticipation in class ac/h / \nучастие в классе ак.ч",
            "Signature / Подпись \npractical work ac/h / \nсамостоятельная работа ак.ч",
          ],
        ]
        : [
          [
            "",
            "Given name(s) / Имя",
            "Surname / Фамилия",
            "Estonian personal code / Эстонский личный код",
            "Signature / Подпись",
          ],
        ];
    autotable(doc, {
      head: head,
      headStyles: {fontSize: 8, font: "PTSans", fontStyle: "normal"},
      body: trainees,
      bodyStyles: {font: "PTSans", fontStyle: "normal"},
      theme: "plain",
      styles: {lineWidth: 0.1, lineColor: "black", minCellHeight: 1.2},
      margin: {top: 100},
      didDrawPage: function (data) {
        if (doc.internal.getNumberOfPages() === 1) {
          data.settings.margin.top = 15;

          doc.addImage(
              images[0],
              "JPG",
              data.settings.margin.left,
              15,
              52.5,
              21,
          );
          doc.addImage(
              images[1],
              "JPG",
              data.settings.margin.left + 115,
              15,
              37,
              21,
          );

          doc.setFontSize(11);
          doc.setFont("PTSans");
          doc.text(
              "Supporting the adaptation of newly arrived foreigners \n" +
              "Поддержка адаптации новых мигрантов\n" +
              "Uussisserändajate kohanemise toetamine\n",
              doc.internal.pageSize.getWidth() / 2,
              45,
              "center",
          );

          doc.setFont("PTSans");
          doc.setFontSize(12);
          doc.text(
              "REGISTRATION FORM / ФОРМА РЕГИСТРАЦИИ",
              doc.internal.pageSize.getWidth() / 2,
              64,
              "center",
          );
          doc.line(14, 66, 283, 66);

          doc.setFont("PTSans");
          doc.setFontSize(11);
          doc.text("Activity / Мероприятия: ", data.settings.margin.left, 74);
          doc.text(
              "Date, time and location / Дата, время и место: ",
              data.settings.margin.left,
              82,
          );
          doc.text(
              "Teacher's name / Имя и фамилия учителя: ",
              data.settings.margin.left,
              90,
          );
          doc.line(14, 95, 283, 95);
        }
        doc.setFontSize(9);
        doc.text(
            doc.internal.getNumberOfPages().toString(),
            doc.internal.pageSize.getWidth() - data.settings.margin.right,
            doc.internal.pageSize.getHeight() - 10,
        );
      },
    });
    doc.save("table.pdf");
  });
}

function getPdfTraineeData() {
  let i = 1;
  return groupData.value.trainees.map((t) => {
    return [i++, t.trainee.firstName, t.trainee.lastName, t.trainee.ssn];
  });
}

function getImages() {
  const images = [];
  const imagePaths = ["kultuurimin_3lovi_est.jpg", "logo_esf_eng.jpg"];
  imagePaths.forEach((path) => {
    images.push(
        new Promise((resolve) => {
          const img = new Image();
          img.src = new URL(`../assets/${path}`, import.meta.url).href;
          img.onload = function () {
            resolve(img);
          };
        }),
    );
  });
  return images;
}

function calculateTotalMinutes(events) {
  return events.reduce((sum, e) => {
    const start = new Date(e.startTime)
    const end = new Date(e.endTime)
    return sum + (end - start) / 60000
  }, 0)
}

async function updateTrainingEvent(event) {
  const payload = getEventPayload(changeDate.value, startTimeNew.value ? moment(startTimeNew.value).format("HH:mm") : "", endTimeNew.value ? moment(endTimeNew.value).format("HH:mm") : "");

  await axios.patch(`/training/event/${event.id}`, payload);
  await getGroupData();
  await getGroupEvents(1);

  expandedEventId.value = null;
  changeDate.value = null;
  startTimeNew.value = null;
  endTimeNew.value = null;

  toast.makeSuccessToast(t("DATA_UPDATE"));
}

const isAddDateValid = computed(() => {
  return isTodayOrFutureDate(changeDateAdd.value);
});

const isStartBeforeEnd = computed(() => {
  if (!startTimeNew.value || !endTimeNew.value) {
    return false;
  }
  const start = moment(startTimeNew.value, "HH:mm");
  const end = moment(endTimeNew.value, "HH:mm");
  return start.isBefore(end);
});

const isStartBeforeEndForAdd = computed(() => {
  if (!startTimeAdd.value || !endTimeAdd.value) return false;
  const start = moment(startTimeAdd.value);
  const end = moment(endTimeAdd.value);
  return start.isBefore(end);
});

const isTodayOrFutureDate = (dateValue) => {
  if (!dateValue) return false;
  const inputDate = moment(dateValue).startOf('day');
  const today = moment().startOf('day');
  return inputDate.isSameOrAfter(today);
};

function getEventPayload(dateValue, timeStartStr, timeEndStr) {
  const timeStart = moment(timeStartStr, "HH:mm");
  const timeEnd = moment(timeEndStr, "HH:mm");
  const date = moment(dateValue);

  const startTime = moment(date).set({
    hour: timeStart.get("hour"),
    minute: timeStart.get("minute"),
  });

  const endTime = moment(date).set({
    hour: timeEnd.get("hour"),
    minute: timeEnd.get("minute"),
  });

  return {
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
  };
}

async function addTrainingEvent() {
  const payload = getEventPayload(changeDateAdd.value, startTimeAdd.value, endTimeAdd.value);
  await axios.post(`training/${groupData.value.id}/events`, payload);
  toast.makeSuccessToast(t("EVENT_ADDED"));
  await getGroupEvents(1);
  changeDateAdd.value = null;
  startTimeAdd.value = null;
  endTimeAdd.value = null;
}

async function removeTrainingEvent(event) {
  await axios.delete(`training/events/${event.id}`);
  trainingEvents.value = trainingEvents.value.filter(e => e.id !== event.id);
  await getGroupEvents(1);
  toast.makeSuccessToast(t('EVENT_REMOVED'));
}

const canSaveEditedEvent = computed(() => {
  return changeDate.value && startTimeNew.value && endTimeNew.value && isStartBeforeEnd.value;
});

const roundedTotalTrainingEventHours = computed(() =>
    Math.round(calculateTotalMinutes(trainingEvents.value) / 45)
);

const totalHoursAllowed = computed(() =>
    groupData.value.lessons * 1.25
);

const isMaxTotalHoursExceed = computed(() => roundedTotalTrainingEventHours.value > totalHoursAllowed.value);
</script>

<style scoped>
.tor-event-item {
  background-color: transparent;
}

.searchForm {
  padding: 20px;
}

.inv-toggle {
  cursor: pointer;
}
</style>
